import { useState, useEffect } from "react";
import { HiPlus } from "react-icons/hi";

const Row = ({ label, children }) => (
  <div className="flex items-center justify-between px-5 py-2.5">
    <span className="text-sm text-gray-500">{label}</span>
    {children}
  </div>
);

const RENEWAL_LABELS = {
  churn_expected: {
    label: "Churn Expected",
    className: "bg-red-50 text-red-700",
  },
  renewal_likely: {
    label: "Renewal Likely",
    className: "bg-emerald-50 text-emerald-700",
  },
  at_risk: { label: "At Risk", className: "bg-amber-50 text-amber-700" },
  unknown: { label: "Unknown", className: "bg-gray-100 text-gray-600" },
};

export const RenewalForecastCard = ({ forecast, onSaveNote }) => {
  const [note, setNote] = useState(forecast.forecastNote || "");
  const [editing, setEditing] = useState(false);

  // Sync note from props when forecast data changes (e.g. after patch)
  useEffect(() => {
    if (!editing) {
      setNote(forecast.forecastNote || "");
    }
  }, [forecast.forecastNote, editing]);

  const renewalConfig =
    RENEWAL_LABELS[forecast.renewalForecast] ?? RENEWAL_LABELS.unknown;

  return (
    <div className="rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-200 px-5 py-4">
        <h2 className="text-base font-semibold text-gray-900">
          Renewal forecast vs expansion potential
        </h2>
      </div>
      <div className="divide-y divide-gray-100">
        <Row label="Renewal forecast">
          <span
            className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${renewalConfig.className}`}
          >
            {renewalConfig.label}
          </span>
        </Row>
        <Row label="Renewal forecast MRR">
          <span className="text-sm font-medium text-gray-900">
            {forecast.renewalMrr}
          </span>
        </Row>
        <Row label="Expansion potential">
          <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">
            {forecast.expansionPotential ? "Yes" : "No"}
          </span>
        </Row>
        <Row label="Expansion potential MRR">
          <span className="text-sm font-medium text-gray-900">
            {forecast.expansionMrr}
          </span>
        </Row>
      </div>
      <div className="border-t border-gray-200 px-5 py-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm text-gray-500">Forecast note</span>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:underline"
            >
              <HiPlus className="size-3" />
              {note ? "Edit note" : "Add note"}
            </button>
          )}
        </div>
        {editing ? (
          <div className="space-y-2">
            <textarea
              className="w-full resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-900 focus:outline-none focus:ring-1 focus:ring-blue-500"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a forecast note..."
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={() => {
                  if (onSaveNote) onSaveNote(note);
                  setEditing(false);
                }}
                className="rounded-md bg-blue-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-blue-700"
              >
                Save
              </button>
              <button
                onClick={() => {
                  setNote(forecast.forecastNote || "");
                  setEditing(false);
                }}
                className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
            <p className="text-xs italic text-gray-400">
              {note ||
                "No notes at this time. Add a note with the add note button."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
